import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCart } from '../context/CartContext';
import { soundEffects } from '../utils/soundEffects';
import ProductCard from '../components/ProductCard';
import Products2Hero from '../components/Products2Hero';
import { generateProductSlug } from '../utils/seoUtils';
import { Helmet } from 'react-helmet-async';
import './LaptopsPage.css';
import './MonitorsPage.css';

const MonitorsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const [monitors, setMonitors] = useState([]);
  const [filteredMonitors, setFilteredMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  
  // Filter states - updated for monitors
  const [filters, setFilters] = useState({
    brand: '',
    priceRange: '',
    screenSize: '',
    availability: '',
    category: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    fetchMonitors();
  }, []);

  useEffect(() => {
    // Check for category and brand parameters in URL
    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category');
    const brandParam = urlParams.get('brand');
    
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      setFilters(prev => ({
        ...prev,
        category: categoryParam
      }));
    }
    
    if (brandParam) {
      setSelectedBrand(brandParam);
      setFilters(prev => ({
        ...prev,
        brand: brandParam
      }));
    }
  }, [location.search]);

  useEffect(() => {
    applyFilters();
  }, [monitors, filters, sortBy]);

  const fetchMonitors = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'products', 'monitors', 'items'));
      const monitorData = querySnapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data();
        const id = docSnapshot.id;
        const slug = generateProductSlug({ id, ...data });
        
        if (!data.slug || data.slug !== slug) {
          updateDoc(doc(db, 'products', 'monitors', 'items', id), { slug })
            .catch(err => console.warn('Monitor slug update failed:', err));
        }
        
        return { id, ...data, slug };
      });
      setMonitors(monitorData);
    } catch (err) {
      setError('Failed to load monitors');
      console.error('Error fetching monitors:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...monitors];

    // Apply filters
    if (filters.category) {
      filtered = filtered.filter(monitor => monitor.category === filters.category);
    }
    if (filters.brand) {
      filtered = filtered.filter(monitor => monitor.brand === filters.brand);
    }
    if (filters.screenSize) {
      filtered = filtered.filter(monitor => monitor.screenSize === filters.screenSize);
    }
    if (filters.availability) {
      filtered = filtered.filter(monitor => monitor.availability === filters.availability);
    }
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(monitor => {
        const price = monitor.price;
        if (max) return price >= min && price <= max;
        return price >= min;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'discount':
        filtered.sort((a, b) => (b.discount || 0) - (a.discount || 0));
        break;
      default:
        // Featured - keep original order
        break;
    }

    setFilteredMonitors(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      priceRange: '',
      screenSize: '',
      availability: '',
      category: ''
    });
    setSelectedCategory('');
    setSelectedBrand('');
    // Clear URL parameters
    navigate('/monitors', { replace: true });
  };

  const handleProductClick = (monitor) => {
    const slug = generateProductSlug(monitor);
    navigate(`/monitor/${slug || monitor.id}`);
  };

  const handleAddToCart = (product) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      imageUrls: product.imageUrls,
      features: [product.screenSize, product.resolution, product.panelType].filter(Boolean),
      quantity: 1
    };

    addToCart(cartItem);
    soundEffects.playAddToCartSound();
  };

  // Get unique filter options
  const getUniqueValues = (key) => {
    return [...new Set(monitors.map(monitor => monitor[key]).filter(Boolean))];
  };

  return (
    <div className="monitors-page">
      <Helmet>
        <title>Premium Refurbished Monitors | Vivid Displays | Nexawave</title>
        <meta name="description" content="Discover premium refurbished monitors at Nexawave Technology. High-quality, vivid displays from top brands at the best prices." />
        <meta name="keywords" content="premium refurbished monitors, used monitors, cheap displays, Nexawave monitors" />
      </Helmet>
      <Products2Hero 
        className="hero-tight"
        tagline="VIVID DISPLAYS · CERTIFIED REFURBISHED · QUALITY GUARANTEED"
        title1="Monitors"
        title2=""
        title3=""
        description=""
        button1Text="Shop Monitors"
        button1Link="#laptops-collection"
      />
      <div className="container" id="laptops-collection">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading monitors...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {/* Filter and Sort Section */}
            <div className="filter-section">
              <div className="filter-header">
                <div className="results-info">
                  <h2>
                    Showing {filteredMonitors.length} {selectedBrand ? selectedBrand : selectedCategory ? selectedCategory.toLowerCase() : ''} monitors
                  </h2>
                  <p>
                    {selectedBrand 
                      ? `Perfect ${selectedBrand} displays for your needs`
                      : selectedCategory 
                        ? `Perfect ${selectedCategory.toLowerCase()} displays for your needs`
                        : 'Find your perfect display'
                    }
                  </p>
                </div>
                <div className="filter-controls">
                  <button 
                    className={`filter-toggle ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
                    </svg>
                    Filters
                  </button>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="newest">Newest First</option>
                    <option value="discount">Best Deals</option>
                  </select>
                </div>
              </div>

              {/* Filter Panel */}
              <div className={`filter-panel ${showFilters ? 'show' : ''}`}>
                <div className="filter-grid">
                  <div className="filter-group">
                    <label>Brand</label>
                    <select 
                      value={filters.brand} 
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                    >
                      <option value="">All Brands</option>
                      {getUniqueValues('brand').map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Screen Size</label>
                    <select 
                      value={filters.screenSize} 
                      onChange={(e) => handleFilterChange('screenSize', e.target.value)}
                    >
                      <option value="">All Sizes</option>
                      {getUniqueValues('screenSize').map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Price Range</label>
                    <select 
                      value={filters.priceRange} 
                      onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                    >
                      <option value="">All Prices</option>
                      <option value="0-10000">Under ₹10,000</option>
                      <option value="10000-20000">₹10,000 - ₹20,000</option>
                      <option value="20000-30000">₹20,000 - ₹30,000</option>
                      <option value="30000-50000">₹30,000 - ₹50,000</option>
                      <option value="50000">Above ₹50,000</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Availability</label>
                    <select 
                      value={filters.availability} 
                      onChange={(e) => handleFilterChange('availability', e.target.value)}
                    >
                      <option value="">All Availability</option>
                      {getUniqueValues('availability').map(availability => (
                        <option key={availability} value={availability}>{availability}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-actions">
                    <button onClick={clearFilters} className="clear-filters">
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="laptops-grid">
              {filteredMonitors.map((monitor) => (
                <div key={monitor.id} className="laptop-card-wrapper">
                  <ProductCard
                    product={{
                      image: monitor.imageUrls?.[0] || '/api/placeholder/300/200',
                      imageAlt: monitor.name,
                      badge: monitor.availability === 'Available' ? 'In Stock' : 'Out of Stock',
                      isNew: false,
                      stockLeft: monitor.stockLeft,
                      brand: monitor.brand || 'NexaWave',
                      name: monitor.name,
                      subtitle: [monitor.condition, monitor.warranty].filter(Boolean).join(' | '),
                      scentNote: [monitor.screenSize, monitor.resolution || 'HD', monitor.panelType || 'LED'].filter(Boolean).join(' | '),
                      rating: Number(monitor.rating) || 4.8,
                      reviews: Number(monitor.reviews) || 120,
                      originalPrice: Number(monitor.originalPrice) || Number(monitor.price) || 0,
                      salePrice: Number(monitor.price) || Number(monitor.originalPrice) || 0
                    }}
                    onCardClick={() => handleProductClick(monitor)}
                    onAddToCart={() => handleAddToCart(monitor)}
                    canAddToCart={monitor.availability === 'Available'}
                    showRating={false}
                    titleTwoLine={true}
                  />
                </div>
              ))}
            </div>

            {filteredMonitors.length === 0 && (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No monitors found</h3>
                <p>Try adjusting your filters to see more results</p>
                <button onClick={clearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MonitorsPage;